import React, { useMemo } from "react";
import { useUserDetails, useAchievementCategories } from "@/api/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Achievement } from "@/views/mario/marioTypes";

const UserDetail = ({ userId } : { userId: string }) => {
    const { data: user, isLoading: isUserLoading } = useUserDetails(userId as string);
    const { data: categoriesData, isLoading: isCategoriesLoading } = useAchievementCategories({});

    // Map achievements by category ID
    const achievementsByCategory = useMemo(() => {
        if (!user) return {};
        const result: Record<number, { achievements: Achievement[]; totalWeight: number }> = {};

        user.achievements.forEach((achievement) => {
            const categoryId = achievement.category.id;
            if (!result[categoryId]) {
                result[categoryId] = { achievements: [], totalWeight: 0 };
            }
            result[categoryId].achievements.push(achievement);
            const weight = achievement.weight || 1;
            result[categoryId].totalWeight += weight;
        });

        return result;
    }, [user]);

    if (isUserLoading || !user || isCategoriesLoading || !categoriesData) {
        return <Skeleton className="h-10 w-full" />;
    }

    return (
        <div>
            <h1 className="text-3xl font-bold mb-6">{user.username}'s Badges</h1>
            <div className="grid grid-cols-1 gap-8">
                {categoriesData.results.map((category) => {
                    const userAchievements = achievementsByCategory[category.id];
                    const totalWeight = userAchievements ? userAchievements.totalWeight : 0;
                    const tiers = category.tiers;

                    return (
                        <div key={category.id} className="border p-4 rounded-lg shadow">
                            <h2 className="text-2xl font-semibold mb-4">{category.name}</h2>
                            {category.short_description && (
                                <p className="text-gray-600 mb-4">{category.short_description}</p>
                            )}
                            <div className="flex flex-wrap mt-4">
                                {tiers.map((tier) => {
                                    const unlocked = totalWeight >= tier.required_achievements;
                                    return (
                                        <div
                                            key={tier.level}
                                            className="mr-6 mb-6 text-center flex flex-col items-center"
                                            style={{ opacity: unlocked ? 1 : 0.4 }}
                                        >
                                            {tier.badge_emoji ? (
                                                <span
                                                    className="text-7xl mb-2"
                                                    role="img"
                                                    aria-label={`Tier ${tier.level}`}
                                                >
                                                    {tier.badge_emoji}
                                                </span>
                                            ) : tier.badge_image ? (
                                                <img
                                                    src={tier.badge_image}
                                                    alt={`Tier ${tier.level}`}
                                                    className="w-20 h-20 mb-2"
                                                />
                                            ) : (
                                                <div className="w-20 h-20 mb-2 bg-gray-200 flex items-center justify-center">
                                                    <span className="text-gray-500">No Badge</span>
                                                </div>
                                            )}
                                            <div className="text-lg font-medium">Tier {tier.level}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default UserDetail;